import { format, parseISO } from 'date-fns';
import API from '@/api';
import V3API from '@/api/v3';
import hasPermission from '@/utils/hasPermission';
import hasGeneralPermission from '@/utils/hasGeneralPermission';
// import hasClubhousePermission from '@/utils/hasClubhousePermission';

import Vuetify from '@/plugins/vuetify';

const RESET_STATE = 'RESET_STATE';

const CREATE_USER = 'CREATE_USER';
const CREATE_USER_SUCCESS = 'CREATE_USER_SUCCESS';
const CREATE_USER_FAILED = 'CREATE_USER_FAILED';

const GET_ME = 'GET_ME';
const GET_ME_SUCCESS = 'GET_ME_SUCCESS';
const GET_MEV3_SUCCESS = 'GET_MEV3_SUCCESS';
const GET_ME_FAILED = 'GET_ME_FAILED';

const GET_PROFILE_IMAGES = 'GET_PROFILE_IMAGES';
const GET_PROFILE_IMAGES_SUCCESS = 'GET_PROFILE_IMAGES_SUCCESS';
const GET_PROFILE_IMAGES_FAILED = 'GET_PROFILE_IMAGES_FAILED';

const GET_USER = 'GET_USER';
const GET_USER_SUCCESS = 'GET_USER_SUCCESS';
const GET_USER_FAILED = 'GET_USER_FAILED';

const GET_CLUB_PERMISSIONS = 'GET_CLUB_PERMISSIONS';
const GET_CLUB_PERMISSIONS_FAILED = 'GET_CLUB_PERMISSIONS_FAILED';
const GET_CLUB_PERMISSIONS_SUCCESS = 'GET_CLUB_PERMISSIONS_SUCCESS';

const GET_STATS = 'GET_STATS';
const GET_STATS_SUCCESS = 'GET_STATS_SUCCESS';
const GET_STATS_FAILED = 'GET_STATS_FAILED';

const GET_CLUB_STATS = 'GET_CLUB_STATS';
const GET_CLUB_STATS_SUCCESS = 'GET_CLUB_STATS_SUCCESS';
const GET_CLUB_STATS_FAILED = 'GET_CLUB_STATS_FAILED';

const GET_USER_STATS = 'GET_USER_STATS';
const GET_USER_STATS_FAILED = 'GET_USER_STATS_FAILED';
const GET_USER_STATS_SUCCESS = 'GET_USER_STATS_SUCCESS';

const UPDATE_ME = 'UPDATE_ME';
const UPDATE_ME_SUCCESS = 'UPDATE_ME_SUCCESS';
const UPDATE_ME_FAILED = 'UPDATE_ME_FAILED';

const CHANGE_PASSWORD = 'CHANGE_PASSWORD';
const CHANGE_PASSWORD_SUCCESS = 'CHANGE_PASSWORD_SUCCESS';
const CHANGE_PASSWORD_FAILED = 'CHANGE_PASSWORD_FAILED';

const SEND_EMAIL = 'SEND_EMAIL';
const SEND_EMAIL_SUCCESS = 'SEND_EMAIL_SUCCESS';
const SEND_EMAIL_FAILED = 'SEND_EMAIL_FAILED';
const RESET_SEND_EMAIL = 'RESET_SEND_EMAIL';

const ACTIVATE_ME = 'ACTIVATE_ME';
const ACTIVATE_ME_SUCCESS = 'ACTIVATE_ME_SUCCESS';
const ACTIVATE_ME_FAILED = 'ACTIVATE_ME_FAILED';

const SET_CLUBHOUSE = 'SET_CLUBHOUSE';
const SET_CLUBHOUSE_SUCCESS = 'SET_CLUBHOUSE_SUCCESS';
const SET_CLUBHOUSE_FAILED = 'SET_CLUBHOUSE_FAILED';

const HAS_PERMISSION = 'HAS_PERMISSION';
const SET_PERMISSION_MESSAGE = 'SET_PERMISSION_MESSAGE';

const SET_LIMITED_USER = 'SET_LIMITED_USER';

const GET_USER_FEED = 'GET_USER_FEED';
const GET_USER_FEED_SUCCESS = 'GET_USER_FEED_SUCCESS';
const GET_USER_FEED_FAILED = 'GET_USER_FEED_FAILED';

const RESET_FEED_LIST = 'RESET_FEED_LIST';

// @ General User Data ==================
const GET_DATA = 'GET_DATA';
const GET_DATA_FAILED = 'GET_DATA_FAILED';
const GET_DATA_SUCCESS = 'GET_DATA_SUCCESS';

// Enabled the module to be namespaced
const namespaced = true;

async function asyncWrap(promise) {
  try {
    const data = await promise;

    if (data.status >= 400) {
      return [undefined, data.data];
    }
    return [data.data, undefined];
  } catch (error) {
    console.error('error:', error);
    return [undefined, error];
  }
}

function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

// user initial state
const state = {
  pendingPOST: false,
  pendingGET: false,
  loadingUser: false,
  loadingMe: false,
  emailSent: false,
  sendingEmail: false,
  activating: false,
  activated: false,
  changingPassword: false,
  passwordChanged: true,
  clubhouse: null,
  isClubHouseOwner: false,
  me: {
    quickStats: {},
  },
  meV3: {},
  users: [],
  user: {
    quickStats: {},
  },
  userStats: {},
  userClubStats: [],
  loadingStats: false,
  loadingClubStats: false,
  userStatsList: {},
  loadingUserStats: false,
  profileImages: [],
  permitted: false,
  permissionMessage: null,
  limitedUser: false,
  clubPermissions: [],
  loadingClubPermissions: false,
  loadingData: false,
  loadingUserFeed: false,
  userFeed: {},
};

// user initial state
const emptyState = {
  pendingPOST: false,
  pendingGET: false,
  loadingUser: false,
  loadingMe: false,
  emailSent: false,
  sendingEmail: false,
  activating: false,
  activated: false,
  changingPassword: false,
  passwordChanged: true,
  clubhouse: null,
  isClubHouseOwner: false,
  me: {
    quickStats: {},
  },
  users: [],
  user: {
    quickStats: {},
  },
  userStats: {},
  loadingStats: false,
  userClubStats: [],
  loadingClubStats: false,
  userStatsList: {},
  loadingUserStats: false,
  profileImages: [],
  permitted: false,
  permissionMessage: null,
  limitedUser: false,
  clubPermissions: [],
  userFeed: {},
};

// User getters
const getters = {
  // getMe returns the data for the authenticated user
  getMe: (state) => {
    return state.me;
  },

  // getMeV3 returns the data for the authenticated user in V3
  getMeV3: (state) => {
    return state.meV3;
  },

  // meV3Challenges returns the data for the user's challenges in V3
  meV3Challenges: (state) => {
    return state.meV3?.challenges ?? [];
  },

  isDarkMode: (state) => {
    return state?.me?.preferences?.darkMode ?? false;
  },

  isLoading: (state) => {
    return state.pendingPOST;
  },

  isLoadingMe: (state) => {
    return state.loadingMe;
  },

  isLoadingUser: (state) => {
    // return state.pendingGET;
    return state.loadingUser;
  },

  isLoadingData: (state) => {
    return state.loadingData;
  },

  // sendingEmail returns a boolean of sending status
  sendingEmail: (state) => {
    return state.sendingEmail;
  },

  // emailSent returns boolean of if the email was sent or not
  emailSent: (state) => {
    return state.emailSent;
  },

  // activating returns a boolean of if the user is in the process of
  // being authenticated or not
  activating: (state) => {
    return state.activating;
  },

  // activated returns a boolean of if the user successfully
  // authenticated or not
  activated: (state) => {
    return state.activated;
  },

  // all returns all users in the store
  all: (state) => {
    return state.users;
  },

  // user returns the current user object in the store
  user: (state) => {
    return state.user;
  },

  // stats returns the current user statistics in the store
  stats: (state) => {
    return state.userStats;
  },

  // userClubStats returns the current user club statistics in the store
  userClubStats: (state) => {
    return state.userClubStats;
  },

  // userStatsList returns the current user club statistics in the store
  userStatsList: (state) => {
    return state.userStatsList;
  },

  // clubPermissions returns the user clubhouse permissions
  clubPermissions: (state) => {
    return state.clubPermissions;
  },

  // userClubMemberships returns the clubhouses a user is a member of
  userClubMemberships: (state) => {
    return state?.meV3?.clubhouse_memberships ?? [];
  },

  // loadingStats returns the status of loading statistics in the store
  loadingStats: (state) => {
    return state.loadingStats;
  },

  // loadingClubStats returns the status of loading user club statistics in the store
  loadingClubStats: (state) => {
    return state.loadingClubStats;
  },

  // loadingUserStats returns the status of loading user club statistics in the store
  loadingUserStats: (state) => {
    return state.loadingUserStats;
  },

  // profileImages returns the authorized user's profile images
  profileImages: (state) => {
    return state.profileImages;
  },

  clubhouse: (state) => {
    return state?.me?.club ?? [];
  },

  // isClubHouseOwner returns if the user is the owner of a clubhouse
  isClubHouseOwner: (state) => {
    return state.isClubHouseOwner;
  },

  getClubhouse: (state) => {
    return state.clubhouse ?? {};
  },

  permitted: (state) => {
    return state.permitted;
  },

  permissionMessage: (state) => {
    return state.permissionMessage;
  },

  hasAnalytics: (state) => {
    return state.me?.permissions ? hasPermission(state.me, ['ADVANALYTICS']) : false;
  },

  hasSeasons: (state) => {
    return state.me?.permissions ? hasPermission(state.me, ['SEASONS']) : false;
  },

  hasReseller: (state) => {
    return state.me?.permissions ? hasPermission(state?.me, ['RESELLER']) : false;
  },

  hasResellerExempt: (state) => {
    return state.me?.permissions ? hasPermission(state?.me, ['RESELLEREXEMPT']) : false;
  },

  hasOEMSeller: (state) => {
    return state.me?.permissions ? hasPermission(state?.me, ['OEMSELLER']) : false;
  },

  limitedUser: (state) => {
    return state.me?.permissions ? hasPermission(state.me, ['LIMITEDUSER']) : false;
  },

  //* ADMIN permission check
  isAdmin: (state) => {
    return state.me?.permissions ? hasPermission(state.me, ['ADMIN']) : false;
  },

  //* ACCOUNTING permission check
  isAccounting: (state) => {
    return state.me?.permissions ? hasPermission(state.me, ['ACCOUNTING']) : false;
  },

  //* DEV permission check
  isDeveloper: (state) => {
    return state.me?.permissions ? hasPermission(state.me, ['DEV']) : false;
  },

  //* SALES permission check
  isSales: (state) => {
    return state.me?.permissions ? hasPermission(state.me, ['SALES']) : false;
  },

  //* SUPPORT permission check
  isSupport: (state) => {
    return state.me?.permissions ? hasPermission(state.me, ['SUPPORT']) : false;
  },

  //* ACTIVATIONS permission check
  isActivations: (state) => {
    return state.me?.permissions ? hasPermission(state.me, ['ACTIVATIONS']) : false;
  },

  canViewAdmin: (state) => {
    return state.me?.permissions
      ? hasPermission(state?.me, [
          'ADMIN',
          'ACCOUNTING',
          'DEV',
          'SALES',
          'SUPPORT',
          'ACTIVATIONS',
          'MARKETING',
        ])
      : false;
  },

  hasClubMetrics: (state) => {
    // console.log('state:', state);
    // return state?.clubPermissions
    //   ? hasClubhousePermission(state?.clubPermissions, ['METRICS'], state?.me)
    //   : false;
  },

  hasClubhouse: (state) => {
    return state.me?.permissions ? hasPermission(state.me, ['CLUBHOUSE']) : false;
  },

  isPartner: (state) => {
    return state.me?.permissions ? hasGeneralPermission(state.me, ['PARTNER']) : false;
  },

  canViewPartnerAdmin: (state) => {
    return state.me?.permissions ? hasGeneralPermission(state?.me, ['PARTNER']) : false;
  },

  currentPartnerID: (state) => {
    const foundPartner = state?.me?.permissions?.find((item) => {
      return item.includes('PARTNER');
    });

    if (foundPartner) {
      return foundPartner?.split('_')?.[1]?.toLowerCase();
    }
    return null;
  },

  partnerPermissions: (state) => {
    const partnerPerms = state?.me?.permissions?.filter((item) => {
      return item.includes('PARTNER');
    });
    return partnerPerms ?? [];
  },

  // TRUSIMAPI permission check
  isAPIPartner: (state) => {
    return state.me?.permissions ? hasGeneralPermission(state.me, ['TRUSIMAPI']) : false;
  },

  isLoadingUserFeed: (state) => {
    return state.loadingUserFeed;
  },

  userFeedList: (state) => {
    return state.userFeed;
  },
};

// User actions
const actions = {
  // * reset state
  async resetState({ commit }) {
    commit(RESET_STATE);
  },

  // * create a user
  async create({ commit, rootState }, payload) {
    let response;

    commit(CREATE_USER);

    try {
      response = await API.users.create(payload);
    } catch (err) {
      response = err;
    }

    return new Promise(async (resolve, reject) => {
      if (response.status >= 400) {
        commit(CREATE_USER_FAILED);
        reject(response);
      } else {
        commit(CREATE_USER_SUCCESS, response.data);
        // set token in localStorage
        await localStorage.setItem('token', response?.data?.authentication?.token);

        // user rootState to set state in another module
        rootState.authentication.isAuthenticated = true;

        // Set user data for raven.
        // Raven.setUserContext({ id: response.data.id, email: response.data.email });

        // Return GET data  within promise
        resolve(response.data);
      }
    });
  },

  //* V3 create a user
  async userCreate({ commit, rootState }, payload) {
    let response;

    commit(CREATE_USER);

    try {
      response = await V3API.users.userCreate(payload);
      // console.log('v3 user create response:', response);
    } catch (err) {
      response = err;
    }

    return new Promise(async (resolve, reject) => {
      if (response.status >= 400) {
        commit(CREATE_USER_FAILED);
        reject(response);
      } else {
        commit(CREATE_USER_SUCCESS, response.data);
        // set token in localStorage
        await localStorage.setItem('token', response?.data?.authentication?.token);

        // user rootState to set state in another module
        rootState.authentication.isAuthenticated = true;

        // Return GET data  within promise
        resolve(response.data);
      }
    });
  },

  // ** me gets the authenticated user's data - Caches data if it exists
  async me({ commit, dispatch, getters, rootGetters, rootState }, payload) {
    const { bypass = false } = payload ?? {};

    // TODO update this with better caching strategy
    let response;
    commit(GET_ME);

    const isAuthenticated = rootGetters['authentication/isAuthenticated'];
    const whitelistToken = rootGetters['authentication/getWhitelistToken'];
    const { getMe = {} } = getters;

    if (getMe?.preferences) {
      Vuetify.framework.theme.isDark = getMe?.preferences?.darkMode ?? false;
    }

    //* Check if user profile was just updated
    const isUpdated = Boolean(localStorage.getItem('userUpdated'));
    //* Get correct me ID value and me object data depending on when/where it was set
    const meID = getMe?.id ? getMe.id : null;
    const meObj = getMe?.id ? getMe : null;
    //* Get correct profile image URL to determine if data is set
    const imgURL = meObj?.profileImage !== undefined ? meObj?.profileImage?.imageURL : undefined;

    //* Custom caching setup for /me endpoint
    //* If we have a current me object in state, return that instead of calling api again
    if (
      !isUpdated &&
      isAuthenticated &&
      !!meID &&
      imgURL !== undefined &&
      imgURL !== '' &&
      !bypass
    ) {
      if (!whitelistToken?.RecordID) {
        await dispatch('authentication/getToken', meID, { root: true });
      }
      commit(GET_ME_SUCCESS, meObj);
      return Promise.resolve(meObj);
    }

    try {
      response = await API.users.me();
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        if (response.status === 401) {
          localStorage.removeItem('token');
        }
        commit(GET_ME_FAILED);
        reject(response);
      } else {
        commit(GET_ME_SUCCESS, response.data);
        const { club } = response.data !== undefined ? response.data : {};

        const limitedUser = hasPermission(response.data, ['LIMITEDUSER']);
        commit(SET_LIMITED_USER, limitedUser);

        if (isUpdated) {
          localStorage.removeItem('userUpdated');
        }

        if (club) {
          const clubhouse = { ...response.data.club[0] };
          // ! setting local storage variable for clubhouse
          localStorage.setItem('club', JSON.stringify(clubhouse));
          dispatch('setClub');
        }

        resolve(response.data);
      }
    });
  },

  // ** V3 me gets the authenticated user's data - Caches data if it exists
  async v3Me({ commit, dispatch, getters, rootGetters, rootState }, payload) {
    let response;
    commit(GET_ME);

    //* turning off all checks as until fully handled in V3

    // const isAuthenticated = rootGetters['authentication/isAuthenticated'];
    // const whitelistToken = rootGetters['authentication/getWhitelistToken'];
    // const { getMeV3 = {} } = getters;

    // if (getMeV3?.preferences) {
    //   // console.log('getMeV3?.preferences:', getMeV3?.preferences);
    //   // Vuetify.framework.theme.isDark = getMeV3?.preferences?.darkMode ?? false;
    //   Vuetify.framework.theme.isDark = getMeV3?.preferences?.dark_mode ?? false;
    // }

    //* Check if user profile was just updated
    // const isUpdated = Boolean(localStorage.getItem('userUpdated'));
    // //* Get correct me ID value and me object data depending on when/where it was set
    // const meID = getMeV3?.id ? getMeV3.id : null;
    // const meObj = getMeV3?.id ? getMeV3 : null;
    // //* Get correct profile image URL to determine if data is set
    // const imgURL = meObj?.profile_image !== undefined ? meObj?.profile_image?.url : undefined;

    // //* Custom caching setup for /me endpoint
    // //* If we have a current me object in state, return that instead of calling api again
    // if (!isUpdated && isAuthenticated && !!meID && imgURL !== undefined && imgURL !== '') {
    //   if (!whitelistToken?.RecordID) {
    //     await dispatch('authentication/getToken', meID, { root: true });
    //   }
    //   commit(GET_MEV3_SUCCESS, meObj);
    //   return Promise.resolve(meObj);
    // }

    try {
      response = await V3API.users.v3GetMe(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        if (response.status === 401) {
          localStorage.removeItem('token');
        }
        commit(GET_ME_FAILED);
        reject(response);
      } else {
        // localStorage.setItem('permissions', JSON.stringify(response.data.permissions));
        commit(GET_MEV3_SUCCESS, response.data);
        // if (isUpdated) {
        //   localStorage.removeItem('userUpdated');
        // }

        resolve(response.data);
      }
    });
  },

  // ** ORIGINAL me gets the authenticated user's data - leaving for reference
  // * Calls api each time and does not cache data.
  // async me({ commit, dispatch, getters, rootGetters }) {
  //   // const isAuthenticated = rootGetters['authentication/isAuthenticated'];
  //   const { getMe } = getters;

  //   if (getMe.preferences) {
  //     Vuetify.framework.theme.isDark = getMe.preferences.darkMode;
  //   }
  //   // const { me } = state;
  //   // const imgURL = getMe?.profileImage !== undefined ? getMe?.profileImage?.imageURL : undefined;

  //   let response;
  //   commit(GET_ME);

  //   //* Disabling this temporarily.
  //   //* Prevents fetching correct user data without logging out after being updated among other things.
  //   // if (isAuthenticated && !!getMe.id && imgURL !== undefined && imgURL !== '') {
  //   //   await dispatch('authentication/getToken', getMe.id, { root: true });
  //   //   commit(GET_ME_SUCCESS, getMe);
  //   //   return Promise.resolve(getMe);
  //   // }

  //   try {
  //     response = await API.users.me();
  //   } catch (err) {
  //     response = err;
  //   }

  //   return new Promise((resolve, reject) => {
  //     if (response.status >= 400) {
  //       if (response.status === 401) {
  //         localStorage.removeItem('token');
  //       }
  //       commit(GET_ME_FAILED);
  //       reject(response);
  //     } else {
  //       commit(GET_ME_SUCCESS, response.data);
  //       const { club } = response.data !== undefined ? response.data : {};

  //       const limitedUser = hasPermission(response.data, ['LIMITEDUSER']);
  //       commit(SET_LIMITED_USER, limitedUser);

  //       if (club) {
  //         const clubhouse = Object.assign({}, response.data.club[0]);
  //         // ! setting local storage variable for clubhouse
  //         localStorage.setItem('club', JSON.stringify(clubhouse));
  //         dispatch('setClub');
  //         // commit(SET_CLUBHOUSE, response.data.club[0])
  //       }

  //       resolve(response.data);
  //     }
  //   });
  // },

  async setClub({ commit, dispatch }) {
    const club = localStorage.getItem('club');
    // let newClub = JSON.parse(club)
    // console.log('setting club')

    // if (newClub) {
    //   console.log('new club')
    //   let data = await dispatch('clubhouses/show', { id: newClub.uuid }, { root: true })
    //     // .then(data => {
    //     //   console.log('data:', data)
    //   commit(SET_CLUBHOUSE_SUCCESS)
    //   commit(SET_CLUBHOUSE, data)
    //     // })
    // }

    if (club) {
      commit(SET_CLUBHOUSE_SUCCESS);
      commit(SET_CLUBHOUSE, club);
    } else {
      commit(SET_CLUBHOUSE_FAILED);
    }
  },

  // * profileImages gets the authenticated user's available profile images
  async profileImages({ commit }) {
    let response;

    commit(GET_PROFILE_IMAGES);

    try {
      response = await API.users.profileImages();

      if (response?.data?.length) {
        //   //* Filtering out apex custom golf ball avatars
        //   response.data = response?.data?.filter((item) => {
        //     return !item?.name?.includes('Golf Ball');
        //   });
        response?.data?.sort((a, b) => {
          return a?.createdAt < b?.createdAt ? 1 : -1;
        });
      }
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_PROFILE_IMAGES_FAILED);
        reject(response);
      } else {
        commit(GET_PROFILE_IMAGES_SUCCESS, response.data);

        resolve(response.data);
      }
    });
  },

  /**
   * show gets a user by id
   */
  async show({ commit }, userID) {
    let response;

    commit(GET_USER);

    try {
      // @ Alternate using async/await for fetching data
      // const [data, err] = await asyncWrap(API.users.show(userID));

      // if (err) {
      //   commit(GET_USER_FAILED);
      //   return;
      // }
      // commit(GET_USER_SUCCESS, data);
      // return;

      response = await API.users.show(userID);
    } catch (err) {
      response = err;
    }

    // TODO account for clubhouse owner permission as well
    // try {
    //   if (hasPermission(response.data, ['ADMIN'])) {
    //     commit(HAS_PERMISSION, true);
    //   }
    // } catch (error) {
    //   if (error.message) {
    //     console.log('error:', error.message);
    //     // The request was made and the server responded with a status code
    //     // that fails out of the range of 2xx
    //     commit(HAS_PERMISSION, false);
    //     commit(SET_PERMISSION_MESSAGE, error.message);
    //     return {
    //       data: error.message,
    //       status: 403,
    //     };
    //   }
    //   // Something happened in setting up the request that triggered an Error
    //   return error;
    // }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        // if (response.userStatus >= 400 && response.statsStatus >= 400) {
        commit(GET_USER_FAILED);
        reject(response);
      } else {
        commit(GET_USER_SUCCESS, response.data);

        resolve(response.data);
      }
    });
  },

  // * clubPermissions fetches the user clubhouse permissions
  async clubPermissions({ commit }) {
    let response = {};

    commit(GET_CLUB_PERMISSIONS);

    try {
      response = await API.users.clubPermissions();
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_CLUB_PERMISSIONS_FAILED);
        reject(response);
      } else {
        commit(GET_CLUB_PERMISSIONS_SUCCESS, response.data);
        resolve(response.data);
      }
    });
  },

  // * stats pulls the user profile statistics from stats_user_list for all time
  async stats({ commit }, payload) {
    let response = {};

    const currentDate = format(parseISO(new Date().toJSON()), 'yyyy-MM-dd');
    payload = {
      user_id: payload,
      from_date: '2015-01-01',
      to_date: currentDate,
      deleteCache: payload?.deleteCache || false,
    };

    commit(GET_STATS);

    try {
      response = await V3API.users.userProfileStatsList(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_STATS_FAILED);
        reject(response);
      } else {
        commit(GET_STATS_SUCCESS, response.data);
        resolve(response.data);
      }
    });
  },

  // * user club stats pulls the user avg club distance statistics
  async userClubStats({ commit }, payload) {
    let response = {};

    commit(GET_CLUB_STATS);

    try {
      response = await API.users.userClubStats(payload);
      if (!response?.data) {
        response.data = [];
      }
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_CLUB_STATS_FAILED);
        reject(response);
      } else {
        commit(GET_CLUB_STATS_SUCCESS, response.data);
        resolve(response.data);
      }
    });
  },

  // * user stats pulls the user distance to pin statistics
  async userStatsList({ commit }, payload) {
    let response = {};
    let dateRange = 'default';

    commit(GET_USER_STATS);

    if (payload.to_date && payload.from_date) {
      dateRange = 'custom';
    }

    try {
      response = await V3API.users.userStatsList(payload);
      if (!response?.data) {
        response.data = {};
      }
      response.data.dateRange = dateRange;
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_USER_STATS_FAILED);
        reject(response);
      } else {
        commit(GET_USER_STATS_SUCCESS, response.data);
        resolve(response.data);
      }
    });
  },

  async getHandicapStats({ commit }, id) {
    let response = {};

    commit(GET_DATA);

    try {
      response = await API.users.handicapStats(id);

      if (!response?.data) {
        response.data = [];
      }
    } catch (error) {
      response = { ...response, error };
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(GET_DATA_SUCCESS, response.data);
        resolve(response.data);
      }
    });
  },

  async resetUserHandicap({ commit }, userID) {
    let response = {};

    commit(GET_DATA);

    try {
      response = await API.users.resetUserHandicap(userID);
      console.log('reset hcp response:', response);
    } catch (error) {
      response = { ...response, error };
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(GET_DATA_SUCCESS, response.data);
        resolve(response.data);
      }
    });
  },

  // * updates the authenticated user
  async update({ commit }, payload) {
    let response;

    commit(UPDATE_ME);

    try {
      response = await API.users.update(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(UPDATE_ME_FAILED);
        reject(response);
      } else {
        localStorage.setItem('userUpdated', true);
        commit(UPDATE_ME_SUCCESS, response.data);

        resolve(response.data);
      }
    });
  },

  // * updates the authenticated user using the v3 endpoint
  async v3Update({ commit }, payload) {
    let response;

    commit(UPDATE_ME);

    try {
      response = await V3API.users.userUpdate(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(UPDATE_ME_FAILED);
        reject(response);
      } else {
        localStorage.setItem('userUpdated', true);
        commit(UPDATE_ME_SUCCESS, response.data);

        resolve(response.data);
      }
    });
  },

  // * changePassword of the authenticated user
  async changePassword({ commit }, payload) {
    let response;

    commit(CHANGE_PASSWORD);

    try {
      response = await API.users.changePassword(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(CHANGE_PASSWORD_FAILED);
        reject(response);
      } else {
        commit(CHANGE_PASSWORD_SUCCESS);

        resolve(response.data);
      }
    });
  },

  // * resetPassword resets password of a user
  async resetPassword({ commit }, payload) {
    let response;

    commit(CHANGE_PASSWORD);

    try {
      response = await API.users.resetPassword(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(CHANGE_PASSWORD_FAILED);
        reject(response);
      } else {
        commit(CHANGE_PASSWORD_SUCCESS);

        resolve(response.data);
      }
    });
  },

  /**
   * sendActivationEmail sends an activation email to the
   * authenticated user
   */
  async sendActivationEmail({ commit }) {
    let response;

    commit(SEND_EMAIL);

    try {
      response = await API.users.sendActivationEmail();
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(SEND_EMAIL_FAILED);
        reject(response);
      } else {
        commit(SEND_EMAIL_SUCCESS);

        resolve();
      }
    });
  },

  /**
   * sendPasswordResetEmail sends an email to the given email about
   * information on resetting their password
   */
  async sendPasswordResetEmail({ commit }, payload) {
    let response;

    commit(SEND_EMAIL);

    try {
      response = await API.users.sendPasswordResetEmail(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(SEND_EMAIL_FAILED);
        reject(response);
      } else {
        commit(SEND_EMAIL_SUCCESS);

        resolve();
      }
    });
  },

  // * activateMe activates the authenticated user
  async activateMe({ commit }, token) {
    let response;

    commit(ACTIVATE_ME);

    try {
      response = await API.users.activate(token);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(ACTIVATE_ME_FAILED);
        reject(response);
      } else {
        commit(ACTIVATE_ME_SUCCESS);

        resolve();
      }
    });
  },

  limitedUserPermission({ commit }, value) {
    commit('SET_LIMITED_USER', value);
  },

  resetActivationEmailState({ commit }) {
    commit('RESET_SEND_EMAIL');
  },

  // * deleteAccount deletes a user account
  async deleteAccount({ commit }, payload) {
    let response;

    commit(GET_DATA);

    try {
      response = await API.users.deleteAccount(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(GET_DATA_SUCCESS);
        resolve(response);
      }
    });
  },

  //* V3 =====================================
  // * userFeedlist returns the user activiting feed
  async userFeedlist({ commit }, payload) {
    let response;

    commit(GET_USER_FEED);

    try {
      response = await V3API.users.v3UserFeedList(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_USER_FEED_FAILED);
        reject(response);
      } else {
        commit(GET_USER_FEED_SUCCESS, response.data);
        resolve(response.data);
      }
    });
  },

  // * test to remov
  async calcHandicap({ commit }, payload) {
    let response;

    try {
      response = await V3API.users.v3CalcHandicap();
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        reject(response);
      } else {
        resolve(response.data);
      }
    });
  },

  //* reset state
  async resetFeedList({ commit }) {
    commit(RESET_FEED_LIST);
  },
};

// * User mutations
const mutations = {
  // * Reset State
  [RESET_STATE](state) {
    state = Object.assign(state, emptyState);
    state.me = {
      quickStats: {},
    };
  },

  // * Reset User feed list
  [RESET_FEED_LIST](state) {
    state.userFeed = {};
    state.userStatsList = {};
  },

  // * Create
  [CREATE_USER](state) {
    state.pendingPOST = true;
  },

  [CREATE_USER_SUCCESS](state, user) {
    state.me = user;
    state.pendingPOST = false;
  },

  [CREATE_USER_FAILED](state) {
    state.pendingPOST = false;
  },

  [SET_CLUBHOUSE_SUCCESS](state) {
    // state.isClubHouseOwner = !!localStorage.getItem('club')
    state.isClubHouseOwner = true;
  },

  [SET_CLUBHOUSE](state, payload) {
    state.clubhouse = JSON.parse(payload);
  },

  [SET_CLUBHOUSE_FAILED](state) {
    state.isClubHouseOwner = false;
  },

  // * Get me
  [GET_ME](state) {
    // state.pendingGET = true;
    state.loadingMe = true;
  },

  [GET_ME_SUCCESS](state, user) {
    state.me = user;
    // state.pendingGET = false;
    state.loadingMe = false;
  },

  [GET_MEV3_SUCCESS](state, user) {
    state.meV3 = user;
    // state.pendingGET = false;
    state.loadingMe = false;
  },

  [GET_ME_FAILED](state) {
    // state.pendingGET = false;
    state.loadingMe = false;
  },

  // * Get me
  [GET_PROFILE_IMAGES](state) {
    state.pendingGET = true;
  },

  [GET_PROFILE_IMAGES_SUCCESS](state, images) {
    state.profileImages = images;
    state.pendingGET = false;
  },

  [GET_PROFILE_IMAGES_FAILED](state) {
    state.pendingGET = false;
  },

  // * Get user
  [GET_USER](state) {
    state.pendingGET = true;
    state.loadingUser = true;
  },

  [GET_USER_SUCCESS](state, user) {
    state.user = user;
    state.pendingGET = false;
    state.loadingUser = false;
  },

  [GET_USER_FAILED](state) {
    state.pendingGET = false;
    state.loadingUser = false;
  },

  // * Get user stats
  [GET_STATS](state) {
    state.loadingStats = true;
  },

  [GET_STATS_SUCCESS](state, stats) {
    state.userStats = stats;
    state.loadingStats = false;
  },

  [GET_STATS_FAILED](state) {
    state.loadingStats = false;
  },

  // * Get user club stats
  [GET_CLUB_STATS](state) {
    state.loadingClubStats = true;
  },

  [GET_CLUB_STATS_SUCCESS](state, stats) {
    state.userClubStats = stats;
    state.loadingClubStats = false;
  },

  [GET_CLUB_STATS_FAILED](state) {
    state.loadingClubStats = false;
  },

  [GET_CLUB_PERMISSIONS](state) {
    state.loadingClubPermissions = true;
  },

  [GET_CLUB_PERMISSIONS_SUCCESS](state, data) {
    state.clubPermissions = data;
    state.loadingClubPermissions = false;
  },

  [GET_CLUB_PERMISSIONS_FAILED](state) {
    state.loadingClubPermissions = false;
  },

  // * Update me
  [UPDATE_ME](state) {
    state.pendingPATCH = true;
  },

  [UPDATE_ME_SUCCESS](state, user) {
    state.me = user;
    state.pendingPATCH = false;
  },

  [UPDATE_ME_FAILED](state) {
    state.pendingPATCH = false;
  },

  // * Change password
  [CHANGE_PASSWORD](state) {
    state.changingPassword = true;
  },

  [CHANGE_PASSWORD_SUCCESS](state, user) {
    state.passwordChanged = true;
    state.changingPassword = false;
  },

  [CHANGE_PASSWORD_FAILED](state) {
    state.changingPassword = false;
  },

  // * Send activation email
  [SEND_EMAIL](state) {
    state.sendingEmail = true;
  },

  [SEND_EMAIL_SUCCESS](state) {
    state.sendingEmail = false;
    state.emailSent = true;
  },

  [SEND_EMAIL_FAILED](state) {
    state.emailSent = false;
    state.sendingEmail = false;
  },

  [RESET_SEND_EMAIL](state) {
    state.emailSent = false;
    state.sendingEmail = false;
  },

  // * Activate me
  [ACTIVATE_ME](state) {
    state.activating = true;
  },

  [ACTIVATE_ME_SUCCESS](state) {
    state.activated = true;
    state.activating = false;
  },

  [ACTIVATE_ME_FAILED](state) {
    state.activating = false;
  },

  [HAS_PERMISSION](state, val) {
    state.permitted = val;
  },

  [SET_PERMISSION_MESSAGE](state, data) {
    state.permissionMessage = data;
  },

  [SET_LIMITED_USER](state, value) {
    state.limitedUser = value;
  },

  // @ General Data
  [GET_DATA](state) {
    state.loadingData = true;
  },

  [GET_DATA_FAILED](state) {
    state.loadingData = false;
  },

  [GET_DATA_SUCCESS](state, data) {
    state.loadingData = false;
  },

  // @ V3
  [GET_USER_FEED](state) {
    state.loadingUserFeed = true;
  },

  [GET_USER_FEED_FAILED](state) {
    state.loadingUserFeed = false;
  },

  [GET_USER_FEED_SUCCESS](state, data) {
    state.loadingUserFeed = false;
    state.userFeed = data;
  },

  // * Get user stats
  [GET_USER_STATS](state) {
    state.loadingUserStats = true;
  },

  [GET_USER_STATS_FAILED](state) {
    state.loadingUserStats = false;
  },

  [GET_USER_STATS_SUCCESS](state, stats) {
    // console.log('stats:', stats);
    state.userStatsList = stats;
    state.loadingUserStats = false;
  },
};

export default {
  namespaced,
  state,
  actions,
  getters,
  mutations,
};
